import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { AlertTriangle, Server, Activity, HardDrive, Cpu, MemoryStick, Clock, Shield, LogOut, Eye, EyeOff, DollarSign, BarChart3 } from 'lucide-react';
import axios from 'axios';

const Dashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [instances, setInstances] = useState([]);
  const [instanceMetrics, setInstanceMetrics] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('servers');
  const [costSummary, setCostSummary] = useState(null);
  const [costByService, setCostByService] = useState([]);
  const [costRange, setCostRange] = useState('30d');
  const [costLoading, setCostLoading] = useState(false);

  const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

  useEffect(() => {
    const token = localStorage.getItem('dashboard_token');
    if (token) {
      setIsAuthenticated(true);
      setAuthHeaders(token);
      fetchAllData();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(fetchAllData, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const setAuthHeaders = (token) => {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const handleLogin = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.post(`${API_BASE}/api/auth/login`, {
        password: loginPassword
      });

      const token = response.data.access_token;
      localStorage.setItem('dashboard_token', token);
      setAuthHeaders(token);
      setIsAuthenticated(true);
      fetchAllData();
    } catch (err) {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dashboard_token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setInstances([]);
    setAlerts([]);
    setSelectedInstance(null);
    setInstanceMetrics([]);
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('dashboard_token');
    if (!token) return;

    setAuthHeaders(token);

    try {
      await Promise.all([
        fetchInstances(),
        fetchAlerts()
      ]);
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      }
    }
  };

  const fetchInstances = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/instances`);
      setInstances(response.data);
    } catch (err) {
      console.error('Failed to fetch instances:', err);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/alerts`);
      setAlerts(response.data);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    }
  };

  const fetchInstanceMetrics = async (instanceId, range = '24h') => {
    try {
      setMetricsLoading(true);
      const response = await axios.get(`${API_BASE}/api/metrics/${instanceId}?range=${range}`);
      setInstanceMetrics(response.data);
    } catch (err) {
      console.error('Failed to fetch instance metrics:', err);
    } finally {
      setMetricsLoading(false);
    }
  };

  const handleInstanceClick = async (instance) => {
    setSelectedInstance(instance);
    await fetchInstanceMetrics(instance.id, timeRange);
  };

  const handleRefreshMetrics = async () => {
    if (selectedInstance) {
      await fetchInstanceMetrics(selectedInstance.id, timeRange);
    }
  };

  const handleTimeRangeChange = async (newRange) => {
    setTimeRange(newRange);
    if (selectedInstance) {
      await fetchInstanceMetrics(selectedInstance.id, newRange);
    }
  };

  const fetchCostSummary = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/costs/summary`);
      setCostSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch cost summary:', err);
    }
  };

  const fetchCostByService = async (range = '30d') => {
    try {
      const response = await axios.get(`${API_BASE}/api/costs/by-service?range=${range}`);
      setCostByService(response.data);
    } catch (err) {
      console.error('Failed to fetch costs by service:', err);
    }
  };

  const fetchCostData = async () => {
    setCostLoading(true);
    await Promise.all([fetchCostSummary(), fetchCostByService(costRange)]);
    setCostLoading(false);
  };

  const handleCostRangeChange = async (newRange) => {
    setCostRange(newRange);
    await fetchCostByService(newRange);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-500 bg-green-50 border-green-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      default: return 'text-gray-500 bg-gray-50 border-gray-200';
    }
  };

  const getScoreColor = (score) => {
    if (score < 50) return 'text-green-600';
    if (score < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatLastSeen = (timestamp) => {
    if (!timestamp) return 'Never';
    const diff = (Date.now() - new Date(timestamp).getTime()) / 1000 / 60;
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${Math.floor(diff)}m ago`;
    return `${Math.floor(diff / 60)}h ago`;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <Shield className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">Server Monitor</h1>
            <p className="text-gray-600 mt-2">Enter password to access dashboard</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter dashboard password"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              onClick={handleLogin}
              disabled={loading || !loginPassword}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Access Dashboard'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Activity className="h-8 w-8 text-blue-600 mr-3" />
              <h1 className="text-xl font-semibold text-gray-900">AWS Server Monitor</h1>
              <div className="ml-8 flex space-x-1">
                <button
                  onClick={() => setActiveTab('servers')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                    activeTab === 'servers'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Server size={14} className="inline mr-1 mb-0.5" />
                  Servers
                </button>
                <button
                  onClick={() => { setActiveTab('costs'); fetchCostData(); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition ${
                    activeTab === 'costs'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <DollarSign size={14} className="inline mr-1 mb-0.5" />
                  Costs
                </button>
              </div>
            </div>

            <button
              onClick={handleLogout}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <LogOut size={16} className="mr-1" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'servers' && (
        <>
        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {instances.map((instance) => (
            <div
              key={instance.id}
              onClick={() => handleInstanceClick(instance)}
              className={`bg-white rounded-lg shadow-sm border-2 p-6 cursor-pointer transition-all hover:shadow-md ${
                selectedInstance?.id === instance.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Server className="h-5 w-5 text-gray-600 mr-2" />
                  <h3 className="font-semibold text-gray-900">{instance.instance_name}</h3>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(instance.status)}`}>
                  {instance.status}
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Scaling Score</span>
                  <span className={`font-bold text-lg ${getScoreColor(instance.scaling_score)}`}>
                    {instance.scaling_score}/100
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="text-center">
                    <Cpu className="h-4 w-4 mx-auto mb-1 text-gray-500" />
                    <div className="font-medium">{instance.cpu_idle_percent}%</div>
                    <div className="text-gray-500">CPU Idle</div>
                  </div>
                  <div className="text-center">
                    <MemoryStick className="h-4 w-4 mx-auto mb-1 text-gray-500" />
                    <div className="font-medium">{instance.memory_available_mb}MB</div>
                    <div className="text-gray-500">Memory</div>
                  </div>
                  <div className="text-center">
                    <HardDrive className="h-4 w-4 mx-auto mb-1 text-gray-500" />
                    <div className="font-medium">{instance.disk_available_gb}GB</div>
                    <div className="text-gray-500">Disk</div>
                  </div>
                </div>

                <div className="flex items-center text-xs text-gray-500 pt-2 border-t">
                  <Clock className="h-3 w-3 mr-1" />
                  Last seen: {formatLastSeen(instance.last_seen)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Alerts */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Alerts</h3>
            <div className="flex items-center text-sm text-gray-500">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {alerts.length} alerts in last 24h
            </div>
          </div>

          <div className="space-y-3">
            {alerts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No alerts in the last 24 hours
              </div>
            ) : (
              alerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className={`h-3 w-3 rounded-full mr-3 ${
                      alert.severity === 'critical' ? 'bg-red-500' :
                      alert.severity === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                    }`}></div>
                    <div>
                      <div className="font-medium text-gray-900">{alert.instance_name}</div>
                      <div className="text-sm text-gray-600">{alert.message}</div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {formatLastSeen(alert.sent_at)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        </>
        )}

        {activeTab === 'costs' && (
          <div>
            {costLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Cost Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">This Month</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ${costSummary?.current_month_total?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <DollarSign className="h-8 w-8 text-green-500" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Last Month</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ${costSummary?.previous_month_total?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <DollarSign className="h-8 w-8 text-gray-400" />
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Month-over-Month</p>
                        <p className={`text-2xl font-bold ${
                          costSummary && costSummary.previous_month_total > 0
                            ? costSummary.current_month_total > costSummary.previous_month_total
                              ? 'text-red-600'
                              : 'text-green-600'
                            : 'text-gray-900'
                        }`}>
                          {costSummary && costSummary.previous_month_total > 0
                            ? `${((costSummary.current_month_total - costSummary.previous_month_total) / costSummary.previous_month_total * 100).toFixed(1)}%`
                            : 'N/A'}
                        </p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-blue-500" />
                    </div>
                  </div>
                </div>

                {/* Daily Cost Trend Chart */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Cost Trend (Last 30 Days)</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={costSummary?.daily_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tickFormatter={(v) => `$${v}`} />
                      <Tooltip
                        formatter={(value) => [`$${value.toFixed(2)}`, 'Cost']}
                        labelFormatter={(d) => new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      />
                      <Bar dataKey="total" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Cost By Service Breakdown */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Cost by Service</h3>
                    <select
                      value={costRange}
                      onChange={(e) => handleCostRangeChange(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                      <option value="90d">Last 90 Days</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    {costByService.map((service, idx) => {
                      const maxCost = costByService[0]?.total || 1;
                      return (
                        <div key={idx} className="flex items-center">
                          <div className="w-48 text-sm text-gray-700 truncate" title={service.service}>
                            {service.service}
                          </div>
                          <div className="flex-1 mx-4">
                            <div className="w-full bg-gray-100 rounded-full h-6">
                              <div
                                className="bg-blue-500 h-6 rounded-full flex items-center justify-end pr-2"
                                style={{ width: `${Math.max((service.total / maxCost) * 100, 8)}%` }}
                              >
                                <span className="text-xs text-white font-medium">${service.total.toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {costByService.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        No cost data available. Run the cost collector first.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Instance Detail Modal with Charts */}
        {selectedInstance && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                      <Server className="h-6 w-6 mr-2 text-blue-600" />
                      {selectedInstance.instance_name} Metrics
                    </h2>
                    <p className="text-gray-600 mt-1">{selectedInstance.instance_type} • Last seen: {formatLastSeen(selectedInstance.last_seen)}</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <select
                      value={timeRange}
                      onChange={(e) => handleTimeRangeChange(e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="1h">Last 1 Hour</option>
                      <option value="6h">Last 6 Hours</option>
                      <option value="24h">Last 24 Hours</option>
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                    </select>

                    <button
                      onClick={handleRefreshMetrics}
                      disabled={metricsLoading}
                      className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-50 text-sm"
                    >
                      {metricsLoading ? 'Loading...' : 'Refresh'}
                    </button>

                    <button
                      onClick={() => {
                        setSelectedInstance(null);
                        setInstanceMetrics([]);
                      }}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Current Status Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <Cpu className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <div className="text-sm text-gray-600">CPU Idle</div>
                        <div className="text-lg font-semibold">{selectedInstance.cpu_idle_percent}%</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <MemoryStick className="h-5 w-5 text-green-600 mr-2" />
                      <div>
                        <div className="text-sm text-gray-600">Memory Available</div>
                        <div className="text-lg font-semibold">{selectedInstance.memory_available_mb} MB</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <HardDrive className="h-5 w-5 text-purple-600 mr-2" />
                      <div>
                        <div className="text-sm text-gray-600">Disk Available</div>
                        <div className="text-lg font-semibold">{selectedInstance.disk_available_gb} GB</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <AlertTriangle className={`h-5 w-5 mr-2 ${getScoreColor(selectedInstance.scaling_score).replace('text-', '')}`} />
                      <div>
                        <div className="text-sm text-gray-600">Scaling Score</div>
                        <div className={`text-lg font-semibold ${getScoreColor(selectedInstance.scaling_score)}`}>
                          {selectedInstance.scaling_score}/100
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts */}
                {metricsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading metrics...</p>
                    </div>
                  </div>
                ) : instanceMetrics.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No metrics data available for this time range</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Resource Usage Chart */}
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Resource Usage</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={instanceMetrics}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="cpu_idle"
                            stroke="#3b82f6"
                            strokeWidth={2}
                            name="CPU Idle %"
                          />
                          <Line
                            type="monotone"
                            dataKey="memory_available"
                            stroke="#10b981"
                            strokeWidth={2}
                            name="Memory Available (MB)"
                          />
                          <Line
                            type="monotone"
                            dataKey="disk_available"
                            stroke="#8b5cf6"
                            strokeWidth={2}
                            name="Disk Available (GB)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Scaling Score Chart */}
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Scaling Score</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={instanceMetrics}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="scaling_score"
                            stroke="#dc2626"
                            strokeWidth={3}
                            name="Scaling Score"
                          />
                          {/* Alert threshold line */}
                          <Line
                            type="monotone"
                            dataKey={() => 70}
                            stroke="#f59e0b"
                            strokeDasharray="5 5"
                            strokeWidth={2}
                            name="Alert Threshold"
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Usage Percentages Chart */}
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Percentages</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={instanceMetrics}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" />
                          <YAxis domain={[0, 100]} />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="memory_used_percent"
                            stroke="#f59e0b"
                            strokeWidth={2}
                            name="Memory Used %"
                          />
                          <Line
                            type="monotone"
                            dataKey="disk_used_percent"
                            stroke="#ef4444"
                            strokeWidth={2}
                            name="Disk Used %"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* CPU Load Chart */}
                    <div className="bg-white border rounded-lg p-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">CPU Load Average</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={instanceMetrics}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="timestamp" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="cpu_load"
                            stroke="#06b6d4"
                            strokeWidth={2}
                            name="CPU Load (1min)"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
